/**
 * The scalar building blocks every DTO is assembled from.
 *
 * Three principles hold throughout this package and are implemented here:
 *
 * - **A schema validates with the domain's own rule, never a second copy of it.**
 *   A date field is accepted only if `isPlainDate` accepts it, and parses to the
 *   branded {@link PlainDate}, so a malformed date cannot reach a rule that would
 *   silently produce a plausible wrong verdict and a parsed DTO needs no further
 *   crossing to be handed to one. A percentage yields {@link PercentText},
 *   canonical decimal text over exact hundredths, never a double (R4.5).
 * - **Normalization happens once, at the boundary.** `''` becomes `null` here
 *   (design.md, "Conventions": every nullable text column carries
 *   `CHECK (col <> '')`), so no service and no repository has to remember to do
 *   it and the R14.6 unset-versus-empty rule stays well defined.
 * - **No user-facing text.** A rejection is reported by field path and a stable
 *   key (R18.9); the words live in the i18next resources. {@link ISSUE_KEYS}
 *   holds the keys this package raises and {@link toFieldIssues} flattens a
 *   `ZodError` into that shape.
 */
import { compareFractions, formatPercent, fractionFromDecimalText, fractionToDecimalText, isPlainDate, parsePercent, percentTextFromNumber, } from '@complan/domain';
import { z } from 'zod';
import { FIELD_PATH_MAX_LENGTH, MAX_ISO_WEEK, MAX_MONTH, MAX_PAGE_SIZE, MAX_PLANNING_YEAR, MIN_ISO_WEEK, MIN_MONTH, MIN_PLANNING_YEAR, REASON_MAX_LENGTH, REASON_MIN_LENGTH, JUSTIFICATION_MAX_LENGTH, JUSTIFICATION_MIN_LENGTH, REFERENCE_LINK_SCHEMES, REFERENCE_LINK_URL_MAX_LENGTH, RULE_ID_MAX_LENGTH, SEARCH_TERM_MAX_LENGTH, } from './limits.js';
/**
 * The keys this package attaches to a rejection it raises itself.
 *
 * Zod's own checks (`min`, `max`, `uuid`) already carry a stable `code`, which
 * {@link toFieldIssues} uses as the key. These are the cases where a custom check
 * would otherwise carry only prose.
 */
export const ISSUE_KEYS = Object.freeze({
    /** Not an ISO 8601 calendar date, or not a real day (`2025-02-30`, `2025-1-5`). */
    dateInvalid: 'date.invalid',
    /** Not a percentage at all (R4.5). */
    percentMalformed: 'percent.malformed',
    /** More than the two decimal places `numeric(5,2)` holds (R4.5). */
    percentTooManyDecimalPlaces: 'percent.too_many_decimal_places',
    /** Outside 0.01 to 100.00 (R4.5). */
    percentOutOfRange: 'percent.out_of_range',
    /** Not an unsigned decimal number at all (Manpower's exact-decimal fields). */
    decimalMalformed: 'decimal.malformed',
    /** More decimal places than the field's column holds. */
    decimalTooManyDecimalPlaces: 'decimal.too_many_decimal_places',
    /** Outside the field's permitted range. */
    decimalOutOfRange: 'decimal.out_of_range',
    /** A reference link address that is not `http` or `https` (R12.9). */
    urlSchemeNotPermitted: 'url.scheme_not_permitted',
    /** A retained target that is not an application-relative path (R1.1). */
    targetPathNotRelative: 'target_path.not_relative',
});
/** Records a rejection as a key, leaving the wording to the i18next resources (R18.9). */
function addKeyedIssue(ctx, key) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, params: { key } });
}
/**
 * A rejection reported by field path and key.
 *
 * The path is dot-joined with array indices in place, matching how the intake
 * form addresses a field: `platforms.0.splitPercent`.
 */
export const fieldIssueSchema = z.object({
    path: z.string().max(FIELD_PATH_MAX_LENGTH),
    key: z.string(),
});
/**
 * Flattens a `ZodError` into {@link FieldIssue}s.
 *
 * The one place a Zod failure becomes a wire value, so the API and the client
 * agree on the path format without either of them re-implementing it. Zod's
 * English default messages are deliberately dropped: a key survives translation
 * and a message does not (R18.9).
 */
export function toFieldIssues(error) {
    return error.issues.map((issue) => {
        const custom = issue.code === z.ZodIssueCode.custom ? issue.params?.['key'] : undefined;
        return {
            path: issue.path.map((segment) => String(segment)).join('.'),
            key: typeof custom === 'string' ? custom : issue.code,
        };
    });
}
/** A `gen_random_uuid()` primary key as it crosses the wire. */
export const idSchema = z.string().uuid();
/** The human-readable Initiative identifier shown in the UI and exports, `INI-000123` (R4.1). */
export const initiativeRefSchema = z.string().regex(/^INI-\d{6,}$/);
/** A stable validation rule identifier, for example `HV-003` (R8.1). */
export const ruleIdSchema = z.string().min(1).max(RULE_ID_MAX_LENGTH);
/** A field path as the intake form addresses it (R8.2, R8.4). */
export const fieldPathSchema = z.string().min(1).max(FIELD_PATH_MAX_LENGTH);
/**
 * A planning date: ISO 8601 `YYYY-MM-DD`, accepted only when it names a real
 * calendar day.
 *
 * Validation is the domain's, not a second regular expression: {@link isPlainDate}
 * rejects the shape violations (`2025-1-5`, `20250105`) and the impossible days
 * (`2025-02-30`, `2025-13-01`) alike, so a date that reaches a rule or the
 * calendar geometry is one the domain would accept.
 *
 * **The parsed type is the branded {@link PlainDate}, not `string`.** The API
 * boundary is where unvalidated date text actually arrives, so it is where the
 * brand has to be applied; a service or repository receiving a parsed DTO then has
 * a value the domain rules accept by type, with no second crossing call to
 * remember and no opportunity for raw text to slip past. Every DTO date field
 * therefore infers as `PlainDate`, and so does every nullable one.
 *
 * The transform is total in the accepting direction: `isPlainDate` narrows the
 * value, so the brand is applied to a value that has passed the domain's own
 * predicate rather than asserted onto an arbitrary string. On failure it raises
 * the keyed issue and yields `z.NEVER`, which is how a Zod transform reports a
 * rejection rather than a value.
 */
export const planningDateSchema = z.string().transform((value, ctx) => {
    if (!isPlainDate(value)) {
        addKeyedIssue(ctx, ISSUE_KEYS.dateInvalid);
        return z.NEVER;
    }
    return value;
});
/** An instant as ISO 8601 with a time-zone offset. Audit and session times only. */
export const timestampSchema = z.string().datetime({ offset: true });
/**
 * A percentage split member, parsed to canonical {@link PercentText} over exact
 * hundredths (R4.5).
 *
 * A JSON number is accepted because that is what a browser form sends, and is
 * converted without rounding: `33.333` is reported as a precision violation
 * rather than quietly becoming `33.33`.
 */
export const percentTextSchema = z
    .union([z.string(), z.number()])
    .transform((raw, ctx) => {
    const text = typeof raw === 'number' ? percentTextFromNumber(raw) : raw;
    const parsed = parsePercent(text);
    if (!parsed.ok) {
        addKeyedIssue(ctx, parsed.failure === 'malformed'
            ? ISSUE_KEYS.percentMalformed
            : parsed.failure === 'too_many_decimal_places'
                ? ISSUE_KEYS.percentTooManyDecimalPlaces
                : ISSUE_KEYS.percentOutOfRange);
        return z.NEVER;
    }
    return formatPercent(parsed.hundredths);
});
/**
 * A non-negative exact-decimal field, generalizing {@link percentTextSchema}
 * beyond percentages: a `numeric(precision, decimalPlaces)` column's range and
 * scale, expressed once so a value outside it is reported before a round trip
 * to the database.
 *
 * Introduced for `complan-manpower`'s Effort_Assumption and Capacity_Period
 * fields and the Field Force demand-input columns (Coverage_Factor 0.00 to
 * 100.00, effort-per-unit-minutes, Impacted_Population_Count and the rest of
 * migrations 18 and 19's `numeric` columns) - none of which fit
 * {@link percentTextSchema}'s fixed 0.01-to-100.00 range or its `PercentText`
 * return type, but all of which need the identical reason `percentTextSchema`
 * itself states for parsing decimal text into exact hundredths rather than a
 * double: a value like `0.001` is a precision violation the user must see, not
 * one to round away. Returns {@link DecimalText} (an alias of the same
 * canonical-decimal-text shape `PercentText` is), the representation
 * `packages/domain/src/manpower/exact-fraction.ts` parses directly with no
 * further conversion.
 *
 * A JSON number is accepted, as {@link percentTextSchema} accepts one, because
 * that is what a browser form sends; it is converted to text without rounding,
 * so excess precision is still reported rather than silently truncated.
 */
export function decimalTextSchema(options) {
    const minFraction = fractionFromDecimalText(options.min.toFixed(options.decimalPlaces));
    const maxFraction = fractionFromDecimalText(options.max.toFixed(options.decimalPlaces));
    const decimalPlacePattern = new RegExp(`^(0|[1-9][0-9]*)(?:\\.([0-9]{1,${String(options.decimalPlaces)}}))?$`);
    return z.union([z.string(), z.number()]).transform((raw, ctx) => {
        const text = typeof raw === 'number' ? String(raw) : raw;
        let fraction;
        try {
            fraction = fractionFromDecimalText(text);
        }
        catch {
            addKeyedIssue(ctx, ISSUE_KEYS.decimalMalformed);
            return z.NEVER;
        }
        if (!decimalPlacePattern.test(text)) {
            addKeyedIssue(ctx, ISSUE_KEYS.decimalTooManyDecimalPlaces);
            return z.NEVER;
        }
        if (compareFractions(fraction, minFraction) < 0 || compareFractions(fraction, maxFraction) > 0) {
            addKeyedIssue(ctx, ISSUE_KEYS.decimalOutOfRange);
            return z.NEVER;
        }
        return fractionToDecimalText(fraction, options.decimalPlaces);
    });
}
/**
 * Required text of a stated length, with leading and trailing whitespace kept.
 *
 * Whitespace is significant for Initiative content: R14.6 defines export equality
 * as identical characters "including leading zeros, leading and trailing spaces
 * and line breaks", so trimming here would break the round trip. Use
 * {@link trimmedTextSchema} for a reason, where R17.2 measures the trimmed length.
 */
export function textSchema(minLength, maxLength) {
    return z.string().min(minLength).max(maxLength);
}
/** Text whose length is measured after trimming, as R17.2 requires of a reason. */
export function trimmedTextSchema(minLength, maxLength) {
    return z.string().trim().min(minLength).max(maxLength);
}
/**
 * Optional text, where unset has exactly one representation: `null`.
 *
 * An absent key, `null` and `''` all parse to `null`, which is the `''`-to-`NULL`
 * ingest normalization of design.md's `NULL`-versus-empty-string decision, done
 * once at the boundary. The length limit is checked on what the client sent, so a
 * 4,001-character Details value is a rejection rather than a truncation.
 *
 * Whitespace-only text is *not* normalized to `null`: `CHECK (col <> '')` accepts
 * `' '`, and R14.6 requires those spaces to survive the export round trip. Only
 * the empty string is unset.
 */
export function nullableTextSchema(maxLength) {
    return z
        .string()
        .max(maxLength)
        .nullable()
        .default(null)
        .transform((value) => (value === null || value === '' ? null : value));
}
/** A reason recorded with a Change_Event: 1 to 500 characters, trimmed (R17.2). */
export const reasonSchema = trimmedTextSchema(REASON_MIN_LENGTH, REASON_MAX_LENGTH);
/**
 * A justifying reason: 10 to 500 characters, trimmed. Required for a
 * confidentiality change (R3.10) and a manual Sales_Cycle override (R7.4).
 */
export const justificationSchema = trimmedTextSchema(JUSTIFICATION_MIN_LENGTH, JUSTIFICATION_MAX_LENGTH);
/**
 * The record version carried by every write (R4.9, R5.8).
 *
 * Starts at 1 and increases by one per persisted change, so zero and negatives
 * are not versions the store can hold.
 */
export const versionSchema = z.number().int().min(1);
/** A planning year, bounded as `sales_cycle.planning_year` is (2000-2100). */
export const planningYearSchema = z.coerce
    .number()
    .int()
    .min(MIN_PLANNING_YEAR)
    .max(MAX_PLANNING_YEAR);
/** A calendar month, 1 to 12 (R11.1). */
export const monthSchema = z.coerce.number().int().min(MIN_MONTH).max(MAX_MONTH);
/** An ISO 8601 week number, 1 to 53 (R10.3, R11.1, R18.10). */
export const isoWeekSchema = z.coerce.number().int().min(MIN_ISO_WEEK).max(MAX_ISO_WEEK);
/**
 * A Boolean that also survives a URL query string, where `true` arrives as text.
 *
 * Only the two literal spellings are accepted: coercing an arbitrary string would
 * make `?ffInvolvement=no` mean yes.
 */
export const booleanFlagSchema = z.union([
    z.boolean(),
    z.literal('true').transform(() => true),
    z.literal('false').transform(() => false),
]);
/** A tri-state filter: `true`, `false`, or unset meaning "do not filter". */
export const booleanFilterSchema = booleanFlagSchema.nullable().default(null);
/**
 * A free-text search term, or `null` meaning "do not filter".
 *
 * Surrounding whitespace is trimmed and a term that is empty once trimmed becomes
 * `null`, so a search box the user typed into and then cleared is indistinguishable
 * from one never touched. Without that, `?search=` would apply a dimension matching
 * every row and the applied-filter metadata (R15.3) would report a filter the user
 * does not have.
 */
export const searchFilterSchema = z
    .union([z.string().max(SEARCH_TERM_MAX_LENGTH), z.null()])
    .transform((value) => {
    if (value === null) {
        return null;
    }
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
})
    .default(null);
/**
 * A reference link address: `http` or `https`, at most 2,048 characters (R12.9).
 *
 * Well-formedness comes from Zod's own URL check; the scheme restriction is
 * stated separately so a `mailto:` or `file:` address is rejected under the key
 * that names the actual rule rather than under a generic malformed-URL key.
 */
export const webAddressSchema = z
    .string()
    .max(REFERENCE_LINK_URL_MAX_LENGTH)
    .url()
    .superRefine((value, ctx) => {
    const scheme = /^[a-z][a-z0-9+.-]*:/i.exec(value)?.[0]?.toLowerCase();
    if (scheme === undefined || !REFERENCE_LINK_SCHEMES.includes(scheme)) {
        addKeyedIssue(ctx, ISSUE_KEYS.urlSchemeNotPermitted);
    }
});
/**
 * The originally requested path and query string, retained across the
 * authentication exchange (R1.1).
 *
 * Application-relative only. A protocol-relative or absolute value would make the
 * retained target an open redirect through a crafted `state`, which is exactly
 * why design.md keeps the target server-side in `oidc_state` rather than in the
 * redirect URL.
 */
export const targetPathSchema = z.string().superRefine((value, ctx) => {
    if (!value.startsWith('/') || value.startsWith('//')) {
        addKeyedIssue(ctx, ISSUE_KEYS.targetPathNotRelative);
    }
});
/**
 * Splits comma-separated filter values so one schema reads a URL query string and
 * a JSON body alike.
 *
 * The calendar screen keeps filter state in the URL (design.md, "Filters and
 * Saved Views") while the export request carries the same filters in a body.
 * Accepting `?platform=a,b`, `?platform=a&platform=b` and `["a","b"]` is what lets
 * {@link ./filters.js#initiativeFilterSchema} be the single filter
 * representation R13.6 depends on.
 */
function splitListValues(value) {
    const split = (text) => text
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part !== '');
    if (typeof value === 'string') {
        return split(value);
    }
    if (Array.isArray(value)) {
        const items = value;
        return items.flatMap((item) => (typeof item === 'string' ? [...split(item)] : [item]));
    }
    return value;
}
/** A multi-value filter dimension: an array of `member`, empty when unfiltered. */
export function listFilterSchema(member) {
    return z.preprocess(splitListValues, z.array(member)).default([]);
}
/** Page window of a paginated read. `limit` defaults to the endpoint's page size. */
export function paginationQuerySchema(defaultLimit) {
    return z.object({
        limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(defaultLimit),
        offset: z.coerce.number().int().min(0).default(0),
    });
}
/**
 * A page of results with the count of entries the requesting user may read.
 *
 * `totalCount` counts readable entries only, so two viewers legitimately see
 * different totals (R3.3, R17.5) and no gap marker betrays a withheld entry.
 */
export function pagedResponseSchema(item) {
    return z.object({
        items: z.array(item),
        totalCount: z.number().int().min(0),
        limit: z.number().int().min(1),
        offset: z.number().int().min(0),
    });
}
//# sourceMappingURL=primitives.js.map